import React from 'react';
import { Link } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';
import { getEvidenceById } from '../lib/evidence';
import { PAGES } from '../lib/site';

const page = PAGES.find((p) => p.path === '/interpretacao-dos-resultados');
const brucki = getEvidenceById('brucki-2003');
const lourenco = getEvidenceById('lourenco-veras-2006');

const ANSWER =
  'A pontuação do Mini Mental (MEEM) vai até 30 pontos, mas o resultado não deve ser classificado por um número isolado. A interpretação depende da referência escolhida, escolaridade, condições da aplicação, funcionalidade e evolução. Resultado abaixo da referência não significa automaticamente demência; resultado preservado não exclui comprometimento cognitivo diante de história clínica consistente.';

const RELATED = [
  { to: '/pontos-de-corte', label: 'Pontos de corte do Mini Mental', note: 'entenda qual referência e qual tipo de número você está usando.' },
  { to: '/escolaridade', label: 'Mini Mental por escolaridade', note: 'veja por que anos de estudo alteram o desempenho esperado.' },
  { to: '/mini-exame-do-estado-mental', label: 'Mini Exame do Estado Mental', note: 'revise o que o MEEM avalia e como o escore é composto.' },
  { to: '/diagnostica-alzheimer', label: 'O Mini Mental diagnostica Alzheimer?', note: 'saiba por que o rastreio não define etiologia.' },
  { to: '/acompanhamento-e-queda-de-pontos', label: 'Queda de pontos no acompanhamento', note: 'compare evolução longitudinal sem depender de um único escore.' }
];

const REFERENCES = [
  `${brucki.citation} DOI: ${brucki.primarySource.value}.`,
  `${lourenco.citation} DOI: ${lourenco.primarySource.value}.`
];

function InlineLink({ to, children }) {
  return <Link to={to} className=" text-[var(--primary)] hover:underline">{children}</Link>;
}

export default function InterpretacaoDosResultados() {
  const sensibilidadeSemEscolaridade = lourenco.results.groups.noFormalEducation.sensitivity;
  const sensibilidadeComEscolaridade = lourenco.results.groups.formalEducation.sensitivity;

  return (
    <ArticleLayout page={page} answer={ANSWER} related={RELATED} references={REFERENCES}>
      <section aria-labelledby="pontuacao-total">
        <h2 id="pontuacao-total" className="text-2xl mb-5">1. Pontuação total do Mini Mental</h2>
        <p className="text-[var(--text-muted)] mb-4">
          O MEEM tradicional soma até <strong>30 pontos</strong>. O total resume o desempenho nas tarefas do instrumento, mas não carrega sozinho um diagnóstico nem uma classificação universal de gravidade.
        </p>
        <p className="text-[var(--text-muted)]">
          Se você já possui os pontos por domínio, use a <InlineLink to="/#calculadora">calculadora Mini Mental online</InlineLink>. A calculadora soma o escore; esta página explica o que fazer com o número depois.
        </p>
      </section>

      <section aria-labelledby="referencia-usada">
        <h2 id="referencia-usada" className="text-2xl mb-5">2. Primeiro: qual referência foi usada?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Antes de interpretar o resultado, identifique o tipo de referência. Em Brucki et al. (2003), os números por escolaridade são <strong>medianas observadas em pessoas saudáveis</strong>; não são cortes diagnósticos. Um limiar estudado para rastreio responde a outra pergunta e depende da população em que foi validado.
        </p>
        <p className="text-[var(--text-muted)]">
          Se essa distinção ainda não estiver clara, veja <InlineLink to="/pontos-de-corte">pontos de corte e referências brasileiras</InlineLink> antes de classificar o resultado.
        </p>
      </section>

      <section aria-labelledby="resultado-alterado">
        <h2 id="resultado-alterado" className="text-2xl mb-5">3. O que significa “resultado alterado”?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Em termos práticos, “alterado” só faz sentido depois de definir <strong>em relação a qual referência</strong>. Um escore abaixo de uma mediana descritiva não equivale a teste diagnóstico positivo; um escore abaixo de um ponto de corte estudado aumenta a suspeita naquele contexto, mas ainda não estabelece causa.
        </p>
        <p className="text-[var(--text-muted)]">
          Escolaridade, idioma, visão, audição, limitação motora, delirium, dor, sedação, doença sistêmica e padronização da aplicação podem deslocar a pontuação. O resultado precisa ser confrontado com queixa, funcionamento cotidiano e história clínica.
        </p>
      </section>

      <section aria-labelledby="abaixo-referencia">
        <h2 id="abaixo-referencia" className="text-2xl mb-5">4. Resultado abaixo da referência</h2>
        <p className="text-[var(--text-muted)] mb-5">
          A primeira leitura não deve ser “há demência”. Um resultado baixo é um sinal para revisar se o número é válido e se faz sentido naquele caso.
        </p>
        <ol className="space-y-3 text-[var(--text-muted)] list-decimal pl-5">
          <li><strong>Aplicação:</strong> confirme se o instrumento foi aplicado e pontuado de forma padronizada.</li>
          <li><strong>Escolaridade:</strong> compare com uma referência compatível com os anos de estudo.</li>
          <li><strong>Visão, audição e limitação motora:</strong> dificuldades para executar tarefas podem reduzir o escore sem representar perda cognitiva equivalente.</li>
          <li><strong>Estado agudo:</strong> delirium, dor, sedação e doença sistêmica podem comprometer o desempenho.</li>
          <li><strong>Queixa e funcionalidade:</strong> verifique se existe declínio percebido e perda de capacidade em atividades previamente independentes.</li>
          <li><strong>Evolução:</strong> quando houver medida anterior comparável, a trajetória do mesmo paciente pode informar mais do que um único valor.</li>
        </ol>
        <p className="text-[var(--text-muted)] mt-5">
          Um escore baixo pode justificar aprofundamento, mas não determina a causa. Veja <InlineLink to="/diagnostica-alzheimer">por que o Mini Mental não diagnostica Alzheimer</InlineLink>.
        </p>
      </section>

      <section aria-labelledby="gravidade">
        <h2 id="gravidade" className="text-2xl mb-5">5. Existe “Mini Mental leve, moderado ou grave” pelo escore?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Não existe uma tabela universal que permita transformar qualquer pontuação do MEEM em “leve”, “moderado” ou “grave” de forma válida para toda população e doença. Classificações desse tipo dependem da condição clínica, da amostra e da referência utilizada.
        </p>
        <p className="text-[var(--text-muted)]">
          Em demência conhecida, o escore pode ajudar a descrever desempenho global e evolução, mas gravidade clínica não deve ser inferida apenas pelo número: funcionalidade, comportamento, sintomas, autonomia e contexto continuam essenciais.
        </p>
      </section>

      <section aria-labelledby="proximo-referencia">
        <h2 id="proximo-referencia" className="text-2xl mb-5">6. Resultado próximo da referência</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Pequenas diferenças em torno de uma mediana ou de um limiar não criam uma fronteira biológica. Um ponto a mais ou a menos pode mudar a posição em relação a uma regra de rastreio sem transformar abruptamente o estado cognitivo da pessoa.
        </p>
        <p className="text-[var(--text-muted)]">
          Nessa zona, evite falsa precisão: escolaridade, condições da aplicação, funcionamento cotidiano, relato de quem convive com o paciente e evolução clínica devem pesar mais do que a aparência de exatidão do número isolado.
        </p>
      </section>

      <section aria-labelledby="acima-referencia">
        <h2 id="acima-referencia" className="text-2xl mb-5">7. Resultado acima da referência</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Um resultado preservado reduz a preocupação em alguns contextos, mas não encerra a investigação quando a história clínica aponta em outra direção. No estudo de Lourenço e Veras (2006), a sensibilidade foi de {String(sensibilidadeSemEscolaridade).replace('.', ',')}% em participantes sem escolaridade formal e {String(sensibilidadeComEscolaridade).replace('.', ',')}% entre aqueles com escolaridade formal. Isso significa que o rastreio não identificou todos os casos presentes naquela população estudada.
        </p>
        <ul className="space-y-3 text-[var(--text-muted)] list-disc pl-5">
          <li>aprofundar quando houver declínio funcional consistente;</li>
          <li>considerar relato confiável de piora cognitiva;</li>
          <li>comparar com avaliações anteriores quando disponíveis;</li>
          <li>não excluir comprometimento leve apenas por escore preservado;</li>
          <li>investigar incompatibilidade entre pontuação e funcionamento observado.</li>
        </ul>
      </section>

      <section aria-labelledby="o-que-fazer-depois">
        <h2 id="o-que-fazer-depois" className="text-2xl mb-5">8. Fluxo de interpretação</h2>
        <div className="p-5 border border-[var(--border)] rounded-2xl bg-[var(--surface-soft)] font-bold leading-relaxed">
          escore → referência → condições da aplicação → escolaridade → queixa/funcionalidade → evolução → aprofundar ou acompanhar
        </div>
        <p className="text-[var(--text-muted)] mt-5">
          Para mudança ao longo do tempo, veja <InlineLink to="/acompanhamento-e-queda-de-pontos">queda de pontos no acompanhamento</InlineLink>. Para limitações de aplicação, consulte <InlineLink to="/adaptacoes-sensoriais">adaptações sensoriais e motoras</InlineLink>.
        </p>
      </section>
    </ArticleLayout>
  );
}
